import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth";
import api, { csrfHeaders } from "../api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { User as UserIcon, Bell, ShieldCheck, CreditCard, FileBadge, Upload, Trash2, CheckCircle2, AlertCircle, Star, Camera, Share2, Facebook, Linkedin, Twitter, Copy, Crown, Sparkles, X, Gift, LogOut } from "lucide-react";
import { redirectToCheckout } from "../lib/checkout";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { QRCodeSVG } from "qrcode.react";
import { useNavigate, Link } from "react-router-dom";
import { useConfirm } from "../components/ConfirmProvider";
import PoolMultiSelect from "../components/PoolMultiSelect";
import ProfileStatusCard from "../components/ProfileStatusCard";
import SafeHtml from "../components/SafeHtml";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";

export default function Profile() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "info");
  // Read URL query param once on mount to sync the initial active tab. Running
  // this effect on every render (no dep array) would keep overriding user
  // navigation between tabs; intentionally empty.
  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    if (t) setActiveTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!user) return null;
  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={user.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=0369A1&color=fff`} alt="" className="w-16 h-16 rounded-full ring-2 ring-sky-200" />
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{user.name}</h1>
          <p className="text-slate-600 text-sm">{user.email} • <span className="uppercase tracking-wide bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full text-xs">{user.role}</span></p>
        </div>
      </div>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="tabs-scroll bg-slate-100 p-1 rounded-xl mb-4">
          <TabsTrigger value="info" data-testid="tab-info"><UserIcon className="w-4 h-4 mr-1" />Informations</TabsTrigger>
          <TabsTrigger value="notifs" data-testid="tab-notifs"><Bell className="w-4 h-4 mr-1" />Notifications</TabsTrigger>
          <TabsTrigger value="payment" data-testid="tab-payment"><CreditCard className="w-4 h-4 mr-1" />Paiement</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices"><FileBadge className="w-4 h-4 mr-1" />Mes factures</TabsTrigger>
          <TabsTrigger value="referral" data-testid="tab-referral"><Gift className="w-4 h-4 mr-1" />Parrainage</TabsTrigger>
          {user.role === "mns" && <TabsTrigger value="subscription" data-testid="tab-subscription"><Crown className="w-4 h-4 mr-1" />Abonnement</TabsTrigger>}
          {user.role === "mns" && <TabsTrigger value="diplomas" data-testid="tab-dip"><FileBadge className="w-4 h-4 mr-1" />Documents</TabsTrigger>}
          {user.role === "mns" && <TabsTrigger value="boost" data-testid="tab-boost"><Star className="w-4 h-4 mr-1" />Visibilité</TabsTrigger>}
          <TabsTrigger value="rgpd" data-testid="tab-rgpd"><ShieldCheck className="w-4 h-4 mr-1" />RGPD</TabsTrigger>
        </TabsList>
        <TabsContent value="info"><InfoTab /></TabsContent>
        <TabsContent value="notifs"><NotifsTab /></TabsContent>
        <TabsContent value="payment"><PaymentTab role={user.role} /></TabsContent>
        <TabsContent value="invoices"><InvoicesTab /></TabsContent>
        <TabsContent value="referral"><ReferralTab /></TabsContent>
        {user.role === "mns" && <TabsContent value="subscription"><SubscriptionTab /></TabsContent>}
        {user.role === "mns" && <TabsContent value="diplomas"><DiplomasTab /></TabsContent>}
        {user.role === "mns" && <TabsContent value="boost"><BoostTab /></TabsContent>}
        <TabsContent value="rgpd"><RgpdTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function useSettings() {
  const [s, setS] = useState(null);
  const reload = () => api.get("/settings").then((r) => setS(r.data));
  useEffect(() => { reload(); }, []);
  const save = async (patch) => {
    const next = { ...(s || {}), ...patch };
    await api.put("/settings", next);
    setS(next);
    toast.success("Enregistré");
  };
  return { s, save, reload };
}

function InfoTab() {
  const { user, refresh } = useAuth();
  const { s, save } = useSettings();
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  // E-invoicing B2B fields (Sept 2026 compliance)
  const [isBusiness, setIsBusiness] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [siren, setSiren] = useState("");
  const [vatNumber, setVatNumber] = useState("");
  const [delivAddress, setDelivAddress] = useState("");
  const [delivPostal, setDelivPostal] = useState("");
  const [delivCity, setDelivCity] = useState("");
  useEffect(() => {
    if (user) { setEmail(user.email || ""); setPhone(user.phone || ""); }
  }, [user]);
  useEffect(() => {
    if (s) {
      if (!phone) setPhone(s.phone || "");
      setAddress(s.address || "");
      setPostalCode(s.postal_code || "");
      setCity(s.city || "");
      setIsBusiness(!!s.is_business);
      setCompanyName(s.company_name || "");
      setSiren(s.siren || "");
      setVatNumber(s.vat_number || "");
      setDelivAddress(s.delivery_address || "");
      setDelivPostal(s.delivery_postal_code || "");
      setDelivCity(s.delivery_city || "");
    }
    // eslint-disable-next-line
  }, [s]);
  if (!s || !user) return <div className="p-8 text-center text-slate-500">Chargement...</div>;
  const billingMissing = !address || !postalCode || !city;

  const handleSave = async () => {
    if (!address.trim()) return toast.error("L'adresse est obligatoire pour la facturation");
    if (!postalCode.trim() || postalCode.replace(/\s/g, "").length < 4) return toast.error("Code postal obligatoire");
    if (!city.trim()) return toast.error("La ville est obligatoire");
    if (isBusiness) {
      if (!companyName.trim()) return toast.error("Raison sociale obligatoire pour facture B2B");
      if (!siren.trim() || siren.replace(/\s/g, "").length !== 9) return toast.error("SIREN obligatoire (9 chiffres) pour facture B2B");
    }
    // Update auth info (email/phone) if changed
    if (email !== user.email || phone !== (user.phone || "")) {
      try {
        await api.put("/auth/me", { email, phone });
        if (refresh) await refresh();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Email ou téléphone invalide");
        return;
      }
    }
    await save({
      phone, address, postal_code: postalCode, city,
      is_business: isBusiness,
      company_name: isBusiness ? companyName : "",
      siren: isBusiness ? siren : "",
      vat_number: isBusiness ? vatNumber : "",
      delivery_address: delivAddress, delivery_postal_code: delivPostal, delivery_city: delivCity,
    });
  };

  return (
    <div className="space-y-4">
      <div className="card-soft p-6 space-y-4">
        <AvatarPicker user={user} onChange={refresh} />
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Nom complet</Label><Input value={user.name} disabled /></div>
          <div>
            <Label>Email <span className="text-red-500">*</span></Label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="email-input" />
            <p className="text-[11px] text-slate-500 mt-0.5">Modifiable à tout moment. Unique sur la plateforme.</p>
          </div>
        </div>
        {billingMissing && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800" data-testid="billing-incomplete">
            ⚠ <strong>Coordonnées de facturation incomplètes</strong> — l'adresse complète (rue, code postal, ville) est obligatoire pour vos factures et le paiement en ligne.
          </div>
        )}
        <div>
          <Label>Adresse <span className="text-red-500">*</span></Label>
          <AddressAutocomplete
            value={address}
            onChange={setAddress}
            onSelect={(it) => {
              if (it.street) setAddress(it.street);
              if (it.postal_code) setPostalCode(it.postal_code);
              if (it.city) setCity(it.city);
            }}
            placeholder="Ex: 10 avenue des Champs-Élysées"
            testId="addr-input"
            required
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label>Code postal <span className="text-red-500">*</span></Label>
            <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} placeholder="75008" maxLength={5} data-testid="postal-input" required />
          </div>
          <div className="sm:col-span-2">
            <Label>Ville <span className="text-red-500">*</span></Label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Paris" data-testid="city-input" required />
          </div>
        </div>
        <div>
          <Label>Téléphone</Label>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="phone-input" />
          <p className="text-[11px] text-slate-500 mt-0.5">Utilisé pour les rappels SMS et la messagerie.</p>
        </div>

        {/* E-invoicing B2B block (Sept 2026 compliance) */}
        <div className="border-t border-slate-200 pt-4 space-y-3" data-testid="b2b-block">
          <div className="flex items-center justify-between">
            <div>
              <Label className="font-semibold">Je facture pour le compte d'une entreprise</Label>
              <p className="text-[11px] text-slate-500 mt-0.5">Active le format <strong>Factur-X</strong> (facturation électronique B2B). Requis si votre employeur paie les cours.</p>
            </div>
            <Switch checked={isBusiness} onCheckedChange={setIsBusiness} data-testid="b2b-toggle" />
          </div>
          {isBusiness && (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Raison sociale <span className="text-red-500">*</span></Label>
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Ex: ACME SAS" data-testid="b2b-company" />
                </div>
                <div>
                  <Label>SIREN <span className="text-red-500">*</span></Label>
                  <Input value={siren} onChange={(e) => setSiren(e.target.value.replace(/\s/g, "").slice(0, 9))} placeholder="9 chiffres" maxLength={9} data-testid="b2b-siren" />
                </div>
                <div className="sm:col-span-2">
                  <Label>Numéro TVA intracommunautaire <span className="text-[10px] text-slate-400">(si assujetti)</span></Label>
                  <Input value={vatNumber} onChange={(e) => setVatNumber(e.target.value.toUpperCase())} placeholder="FR + 11 chiffres" maxLength={13} data-testid="b2b-vat" />
                </div>
              </div>
              <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900">
                <strong>Adresse de livraison</strong> (obligatoire si différente de l'adresse de facturation) :
                <div className="grid sm:grid-cols-3 gap-2 mt-2">
                  <Input value={delivAddress} onChange={(e) => setDelivAddress(e.target.value)} placeholder="Rue" className="sm:col-span-3" data-testid="b2b-deliv-addr" />
                  <Input value={delivPostal} onChange={(e) => setDelivPostal(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))} placeholder="CP" maxLength={5} data-testid="b2b-deliv-postal" />
                  <Input value={delivCity} onChange={(e) => setDelivCity(e.target.value)} placeholder="Ville" className="sm:col-span-2" data-testid="b2b-deliv-city" />
                </div>
              </div>
            </>
          )}
        </div>

        <Button className="rounded-full bg-sky-700 hover:bg-sky-600" onClick={handleSave} data-testid="save-info">Enregistrer</Button>
      </div>
      <PasswordChangeCard />
      {user.role === "mns" && <MnsPublicInfo />}
      {user.role === "mns" && <MnsAdminInfo />}
      {user.role === "mns" && <ShareProfile mnsId={user.user_id} />}
    </div>
  );
}

function PasswordChangeCard() {
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!current || !next1 || !next2) return toast.error("Tous les champs sont requis");
    if (next1.length < 6) return toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
    if (next1 !== next2) return toast.error("La confirmation ne correspond pas");
    if (next1 === current) return toast.error("Le nouveau mot de passe doit être différent de l'actuel");
    setBusy(true);
    try {
      await api.post("/auth/password", { current_password: current, new_password: next1 });
      toast.success("Mot de passe mis à jour");
      setCurrent(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Modification impossible");
    } finally { setBusy(false); }
  };

  const inputType = show ? "text" : "password";
  return (
    <form onSubmit={submit} className="card-soft p-6 space-y-4" data-testid="password-change-card">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-slate-900 text-lg" style={{ fontFamily: "Outfit" }}>Mot de passe</h3>
          <p className="text-xs text-slate-500">Modifiez votre mot de passe de connexion. Min. 6 caractères.</p>
        </div>
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="text-xs text-sky-700 hover:underline"
          data-testid="password-show-toggle"
        >
          {show ? "Masquer" : "Afficher"}
        </button>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-sm">Mot de passe actuel <span className="text-red-500">*</span></Label>
          <Input
            type={inputType}
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            data-testid="password-current"
          />
        </div>
        <div>
          <Label className="text-sm">Nouveau mot de passe <span className="text-red-500">*</span></Label>
          <Input
            type={inputType}
            value={next1}
            onChange={(e) => setNext1(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            data-testid="password-new"
          />
        </div>
        <div>
          <Label className="text-sm">Confirmer <span className="text-red-500">*</span></Label>
          <Input
            type={inputType}
            value={next2}
            onChange={(e) => setNext2(e.target.value)}
            autoComplete="new-password"
            minLength={6}
            data-testid="password-confirm"
          />
        </div>
      </div>
      {next1 && next2 && next1 !== next2 && (
        <p className="text-xs text-red-600" data-testid="password-mismatch">⚠ Les deux nouveaux mots de passe ne correspondent pas.</p>
      )}
      <Button
        type="submit"
        disabled={busy}
        className="rounded-full bg-sky-700 hover:bg-sky-600"
        data-testid="password-submit"
      >
        {busy ? "Mise à jour…" : "Changer mon mot de passe"}
      </Button>
    </form>
  );
}

function AvatarPicker({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const submit = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image trop volumineuse (max 5 Mo)");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/avatar`, {
        method: "POST", body: fd, credentials: "include",
        headers: csrfHeaders(),
      });
      if (!res.ok) throw new Error();
      toast.success("Photo de profil mise à jour");
      if (onChange) await onChange();
    } catch { toast.error("Échec du téléversement"); }
    finally { setBusy(false); }
  };
  const src = user?.picture
    ? (user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`)
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || "User")}&background=0369A1&color=fff&size=200`;
  return (
    <div className="flex items-center gap-4 pb-4 border-b" data-testid="avatar-picker">
      <img src={src} alt="" className="w-20 h-20 rounded-full object-cover ring-4 ring-sky-100" />
      <div className="flex-1">
        <Label className="block text-sm">Photo de profil</Label>
        <p className="text-xs text-slate-500 mb-1">JPG/PNG/WebP — 5 Mo max.</p>
        <label className="inline-flex items-center gap-1 cursor-pointer text-sm text-sky-700 font-medium hover:underline">
          <Camera className="w-4 h-4" />{busy ? "Envoi..." : "Modifier la photo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => submit(e.target.files?.[0])} data-testid="profile-avatar-input" />
        </label>
      </div>
    </div>
  );
}

function MnsPublicInfo() {
  const [profile, setProfile] = useState(null);
  const [bio, setBio] = useState("");
  const [atHome, setAtHome] = useState(false);
  const [poolIds, setPoolIds] = useState([]);
  useEffect(() => {
    api.get("/profile/mns").then((r) => {
      setProfile(r.data); setBio(r.data?.bio || "");
      setAtHome(!!r.data?.at_home_available);
      setPoolIds(r.data?.attached_pool_ids || []);
    });
  }, []);
  const save = async () => {
    // Iter35: PUT does a partial update — only send the fields owned by this section
    // Iter39: hourly_rate no longer sent from UI (managed per-slot).
    await api.put("/profile/mns", {
      bio,
      at_home_available: atHome,
      attached_pool_ids: poolIds,
    });
    toast.success("Description publique enregistrée");
    const fresh = await api.get("/profile/mns"); setProfile(fresh.data);
  };
  if (!profile) return null;
  return (
    <div className="card-soft p-6 space-y-4" data-testid="mns-public-info">
      <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Description publique</h3>
      <p className="text-xs text-slate-500">Ces informations sont visibles par les clients sur votre fiche publique.</p>
      <div>
        <Label>Présentation / bio</Label>
        <textarea
          className="w-full border rounded-xl p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-sky-200"
          value={bio} onChange={(e) => setBio(e.target.value)}
          placeholder="Ex: MNS depuis 8 ans, spécialisée bébé-nageur et adulte débutant. Bienveillance et progression à votre rythme."
          data-testid="profile-bio-input"
        />
      </div>
      {/* Iter39: hourly_rate field removed — the price is now defined per slot.
          The MNS public card displays a price range automatically (min / max). */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-slate-700">
        💡 <strong>Tarif :</strong> définissez le prix de chaque cours directement lors de la publication d'un créneau dans <em>Planning</em>.
        Votre fiche publique affichera automatiquement la fourchette de prix constatée sur vos créneaux à venir.
      </div>

      {/* Iter35 wave 2: At-home availability checkbox */}
      <div>
        <Label>Cours à domicile</Label>
        <label className="flex items-start gap-3 bg-slate-50 hover:bg-sky-50 transition-colors rounded-xl p-3 mt-1 cursor-pointer" data-testid="profile-athome-label">
          <input
            type="checkbox"
            className="mt-0.5 w-4 h-4 accent-sky-700"
            checked={atHome}
            onChange={(e) => setAtHome(e.target.checked)}
            data-testid="profile-athome-toggle"
          />
          <span className="text-sm">
            <strong>Je propose des cours à domicile</strong>
            <span className="block text-xs text-slate-500 mt-0.5">Un badge "À domicile" apparaîtra sur votre fiche publique en plus de la liste des établissements.</span>
          </span>
        </label>
      </div>

      {/* Iter35 wave 2: Multi-establishment selector */}
      <div>
        <Label>Établissements où vous proposez des cours</Label>
        <p className="text-xs text-slate-500 mb-2">
          Recherchez les piscines / centres aquatiques dans lesquels vous intervenez.
          Aucun résultat ? <Link to="/map?add=1" className="text-sky-700 hover:underline" data-testid="profile-pools-add-manual">Ajoutez l'établissement manuellement →</Link>
        </p>
        <PoolMultiSelect value={poolIds} onChange={setPoolIds} testId="profile-pools" />
      </div>

      <Button onClick={save} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="save-public-info">Enregistrer</Button>
    </div>
  );
}

function MnsAdminInfo() {
  const [profile, setProfile] = useState(null);
  const [siret, setSiret] = useState("");
  const [vatSubject, setVatSubject] = useState(false);
  const [vatRate, setVatRate] = useState(20);
  const [vatOptionDebits, setVatOptionDebits] = useState(false);
  const [naf, setNaf] = useState("");
  useEffect(() => {
    api.get("/profile/mns").then((r) => {
      setProfile(r.data);
      setSiret(r.data?.siret || ""); setVatSubject(!!r.data?.vat_subject); setVatRate(r.data?.vat_rate || 20);
      setVatOptionDebits(!!r.data?.vat_option_debits);
      setNaf(r.data?.activity_code_naf || "");
    });
  }, []);
  const save = async () => {
    if (!siret || siret.length < 9) return toast.error("Numéro SIRET requis (14 chiffres)");
    // Iter35: partial update — only the admin fields
    await api.put("/profile/mns", {
      siret, vat_subject: vatSubject, vat_rate: vatRate,
      vat_option_debits: vatOptionDebits, activity_code_naf: naf,
    });
    toast.success("Informations administratives enregistrées");
    const fresh = await api.get("/profile/mns"); setProfile(fresh.data);
  };
  if (!profile) return null;
  const completeness = profile.completeness;
  return (
    <div className="card-soft p-6 space-y-3" data-testid="mns-admin-info">
      <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Informations administratives</h3>
      {completeness && !completeness.complete && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-2 text-xs text-amber-800">⚠ Profil incomplet — manque : {completeness.missing.join(", ")}.</div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Numéro SIRET <span className="text-red-500">*</span></Label>
          <Input required value={siret} onChange={(e) => setSiret(e.target.value.replace(/\s/g, ""))} placeholder="14 chiffres" maxLength={14} data-testid="profile-siret-input" />
        </div>
        <div>
          <Label>Code NAF / APE <span className="text-[10px] text-slate-400">(optionnel)</span></Label>
          <Input value={naf} onChange={(e) => setNaf(e.target.value.toUpperCase())} placeholder="Ex: 9319Z" maxLength={5} data-testid="profile-naf-input" />
        </div>
        <div>
          <Label>Soumis à la TVA ?</Label>
          <div className="flex items-center gap-3 bg-slate-50 p-2 rounded-xl mt-1">
            <Switch checked={vatSubject} onCheckedChange={setVatSubject} data-testid="profile-vat-toggle" />
            <span className="text-sm">{vatSubject ? "Oui" : "Non — franchise en base"}</span>
          </div>
        </div>
        {vatSubject && (
          <div>
            <Label>Taux de TVA (%)</Label>
            <Input type="number" step="0.1" min="0" value={vatRate} onChange={(e) => setVatRate(parseFloat(e.target.value))} data-testid="profile-vat-rate-input" />
          </div>
        )}
        <div className="sm:col-span-2">
          <Label>Option TVA sur les débits</Label>
          <div className={`flex items-center gap-3 p-2 rounded-xl mt-1 ${vatSubject ? "bg-slate-50" : "bg-slate-100 opacity-60"}`}>
            <Switch checked={vatOptionDebits} onCheckedChange={setVatOptionDebits} disabled={!vatSubject} data-testid="profile-vat-debits-toggle" />
            <span className="text-xs text-slate-600">
              {!vatSubject ? "Activez d'abord « Soumis à la TVA » pour configurer cette option." :
                vatOptionDebits ? "Oui — TVA exigible à l'émission de la facture" : "Non — TVA exigible à l'encaissement (par défaut)"}
            </span>
          </div>
        </div>
      </div>
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-xs text-sky-900" data-testid="einv-banner-mns">
        <strong>📄 Facturation électronique (obligation française dès sept. 2026)</strong><br />
        Vos factures sont automatiquement générées au format <strong>Factur-X (PDF/A-3 + CII XML EN 16931)</strong>, conforme à la réforme. Vous n'avez rien à faire — le téléchargement est disponible depuis le tableau de bord client dès que la présence est validée.
      </div>
      <p className="text-[11px] text-slate-500">Ces informations sont visibles dans les mentions légales de votre fiche publique (obligation légale).</p>
      <Button onClick={save} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="save-admin-info">Enregistrer</Button>
    </div>
  );
}

function ShareProfile({ mnsId }) {
  const url = `${window.location.origin}/mns/${mnsId}`;
  const text = encodeURIComponent("Réservez vos cours de natation avec moi sur MySwimmingLessons !");
  const enc = encodeURIComponent(url);
  const qrRef = useRef(null);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié dans le presse-papier"); }
    catch { toast.error("Impossible de copier"); }
  };
  const getSvgAsPng = async () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return null;
    const xml = new XMLSerializer().serializeToString(svg);
    const svg64 = btoa(unescape(encodeURIComponent(xml)));
    return await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const c = document.createElement("canvas");
        const size = 512; c.width = size; c.height = size;
        const ctx = c.getContext("2d");
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, 0, 0, size, size);
        c.toBlob((b) => resolve(b), "image/png");
      };
      img.src = "data:image/svg+xml;base64," + svg64;
    });
  };
  const downloadQR = async () => {
    const blob = await getSvgAsPng();
    if (!blob) return toast.error("QR code introuvable");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = `myswimminglessons_qr_${mnsId}.png`; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    toast.success("QR code téléchargé");
  };
  const copyQR = async () => {
    const blob = await getSvgAsPng();
    if (!blob) return toast.error("QR code introuvable");
    try {
      if (window.ClipboardItem && navigator.clipboard?.write) {
        await navigator.clipboard.write([new window.ClipboardItem({ "image/png": blob })]);
        toast.success("QR code copié");
      } else {
        toast.info("Navigateur incompatible — téléchargement à la place"); downloadQR();
      }
    } catch {
      toast.info("Copie refusée — téléchargement à la place"); downloadQR();
    }
  };
  const links = [
    { name: "Facebook", icon: Facebook, color: "hover:bg-blue-50 hover:text-blue-600", url: `https://www.facebook.com/sharer/sharer.php?u=${enc}` },
    { name: "Twitter / X", icon: Twitter, color: "hover:bg-slate-50", url: `https://twitter.com/intent/tweet?url=${enc}&text=${text}` },
    { name: "LinkedIn", icon: Linkedin, color: "hover:bg-sky-50 hover:text-sky-700", url: `https://www.linkedin.com/sharing/share-offsite/?url=${enc}` },
    { name: "WhatsApp", icon: Share2, color: "hover:bg-emerald-50 hover:text-emerald-600", url: `https://wa.me/?text=${text}%20${enc}` },
    { name: "Email", icon: Share2, color: "hover:bg-slate-50", url: `mailto:?subject=Mon%20profil%20MySwimmingLessons&body=${text}%20${enc}` },
  ];
  return (
    <div className="card-soft p-6" data-testid="share-profile">
      <div className="flex items-center gap-2 mb-3">
        <Share2 className="w-5 h-5 text-sky-700" />
        <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Partager mon profil</h3>
      </div>
      <p className="text-sm text-slate-600 mb-3">Partagez votre fiche publique avec vos élèves et sur vos réseaux pour développer votre activité.</p>

      <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
        <div className="flex flex-col items-center gap-2" data-testid="mns-qr">
          <div ref={qrRef} className="p-3 bg-white border-2 border-sky-100 rounded-2xl shadow-sm">
            <QRCodeSVG value={url} size={168} level="M" includeMargin={false}
              bgColor="#ffffff" fgColor="#0c4a6e" />
          </div>
          <div className="text-[11px] text-slate-500">QR code unique — scannez-moi</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full" onClick={copyQR} data-testid="qr-copy-btn"><Copy className="w-3 h-3 mr-1" />Copier</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={downloadQR} data-testid="qr-download-btn"><Camera className="w-3 h-3 mr-1" />PNG</Button>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <Input value={url} readOnly className="text-xs" data-testid="share-url" />
            <Button variant="outline" size="sm" className="rounded-full whitespace-nowrap" onClick={copy} data-testid="share-copy-btn"><Copy className="w-3 h-3 mr-1" />Copier</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {links.map((l) => (
              <a key={l.name} href={l.url} target="_blank" rel="noopener noreferrer"
                 className={`inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-sm transition ${l.color}`}
                 data-testid={`share-${l.name.toLowerCase().replace(/\s.*/, "")}`}>
                <l.icon className="w-3.5 h-3.5" />{l.name}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function NotifsTab() {
  const { s, save } = useSettings();
  const [kinds, setKinds] = useState({});
  const [prefs, setPrefs] = useState({});
  const [dirty, setDirty] = useState(false);
  useEffect(() => { api.get("/settings/kinds").then((r) => setKinds(r.data)); }, []);
  useEffect(() => {
    if (s?.notification_prefs) setPrefs(s.notification_prefs);
    else if (s) {
      const dflt = {};
      Object.keys(kinds).forEach((k) => { dflt[k] = { email: !!s.notifications_email, push: !!s.notifications_push }; });
      setPrefs(dflt);
    }
    setDirty(false);
  }, [s, kinds]);

  const update = (kind, channel, val) => {
    setPrefs((p) => ({ ...p, [kind]: { ...(p[kind] || {}), [channel]: val } }));
    setDirty(true);
  };
  const submit = async () => {
    await save({ notification_prefs: prefs });
    setDirty(false);
    toast.success("Préférences enregistrées");
  };

  if (!s) return null;
  return (
    <div className="card-soft p-6 space-y-4">
      <p className="text-sm text-slate-600">Choisissez par catégorie sur quel canal vous souhaitez recevoir vos alertes.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="notif-prefs-table">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-slate-500 border-b">
              <th className="text-left py-2 pr-3 font-semibold">Catégorie</th>
              <th className="py-2 px-2 font-semibold w-24 text-center">Email</th>
              <th className="py-2 px-2 font-semibold w-24 text-center">Push (in-app)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(kinds).map(([k, label]) => (
              <tr key={k} className="border-b">
                <td className="py-3 pr-3 text-slate-800">{label}</td>
                <td className="py-3 px-2 text-center">
                  <Switch checked={!!prefs[k]?.email} onCheckedChange={(v) => update(k, "email", v)} data-testid={`pref-${k}-email`} />
                </td>
                <td className="py-3 px-2 text-center">
                  <Switch checked={!!prefs[k]?.push} onCheckedChange={(v) => update(k, "push", v)} data-testid={`pref-${k}-push`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button onClick={submit} disabled={!dirty} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="save-notifs">
        Enregistrer les modifications
      </Button>
    </div>
  );
}

function PaymentTab({ role }) {
  const { s, save } = useSettings();
  const [form, setForm] = useState({});
  useEffect(() => { if (s) setForm(s); }, [s]);
  if (!s) return null;
  const upd = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="card-soft p-6 space-y-4">
      {role === "client" ? (
        <>
          <p className="text-sm text-slate-600">Vos paiements sont sécurisés par Stripe. Nous ne stockons que les informations d'affichage.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Titulaire de la carte</Label><Input value={form.bank_card_holder || ""} onChange={(e) => upd("bank_card_holder", e.target.value)} data-testid="card-holder" /></div>
            <div><Label>4 derniers chiffres</Label><Input maxLength={4} value={form.bank_card_last4 || ""} onChange={(e) => upd("bank_card_last4", e.target.value.replace(/\D/g, ""))} data-testid="card-last4" /></div>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600">IBAN où les retraits de votre cagnotte seront virés.</p>
          <div><Label>IBAN</Label><Input value={form.iban || ""} onChange={(e) => upd("iban", e.target.value.toUpperCase())} placeholder="FR76 ..." data-testid="iban" /></div>
          <div><Label>BIC</Label><Input value={form.bic || ""} onChange={(e) => upd("bic", e.target.value.toUpperCase())} placeholder="BNPAFRPP" data-testid="bic" /></div>
        </>
      )}
      <Button className="rounded-full bg-sky-700 hover:bg-sky-600" onClick={() => save(form)} data-testid="save-payment">Enregistrer</Button>
    </div>
  );
}

function DiplomasTab() {
  const [list, setList] = useState([]);
  const [required, setRequired] = useState([]);
  const [uploadingFor, setUploadingFor] = useState(null); // { doc_type, label }
  const [form, setForm] = useState({ name: "", valid_until: "", file: null });

  // Iter37 perf: pre-compute filtered slices to avoid re-running filter() on every render
  const requiredDocs = useMemo(() => required.filter((r) => r.required !== false), [required]);
  const optionalDocs = useMemo(() => required.filter((r) => r.required === false), [required]);

  const reload = async () => {
    const [a, b] = await Promise.all([
      api.get("/diplomas"),
      api.get("/diplomas/required"),
    ]);
    setList(a.data);
    setRequired(b.data);
  };
  useEffect(() => { reload(); }, []);

  const latestByType = (dt) => {
    const matches = list.filter((d) => d.doc_type === dt);
    return matches.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  };

  const startUpload = (doc_type, label, requires_expiry = true) => {
    setUploadingFor({ doc_type, label, requires_expiry });
    setForm({ name: label, valid_until: "", file: null });
    // Iter35 wave 2: scroll directly to the upload box for an instant UX feedback
    setTimeout(() => {
      const el = document.querySelector('[data-testid="doc-upload-form"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Briefly highlight
      el?.classList.add("ring-4", "ring-sky-200");
      setTimeout(() => el?.classList.remove("ring-4", "ring-sky-200"), 1600);
    }, 50);
  };

  const submit = async (e) => {
    e.preventDefault();
    const needsExpiry = uploadingFor?.requires_expiry !== false;
    if (!form.file) return toast.error("Fichier requis");
    if (needsExpiry && !form.valid_until) return toast.error("Date de validité requise");
    try {
      const fd = new FormData(); fd.append("file", form.file);
      const params = new URLSearchParams({
        name: form.name || uploadingFor.label,
        valid_until: needsExpiry ? form.valid_until : "",
        doc_type: uploadingFor.doc_type,
      }).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/diplomas/upload?${params}`, {
        method: "POST", body: fd,
        headers: csrfHeaders(),
        credentials: "include",
      });
      if (!res.ok) throw new Error("upload failed");
      toast.success("Document téléversé — en attente de validation admin");
      setUploadingFor(null);
      reload();
    } catch {
      toast.error("Échec du téléversement");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await api.delete(`/diplomas/${id}`); reload();
  };

  const STATUS_BADGE = (d) => {
    if (!d) return <span className="text-xs px-2 py-1 rounded-full bg-slate-200 text-slate-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Non fourni</span>;
    if (d.validation_status === "validated") return <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Validé</span>;
    if (d.validation_status === "rejected") return <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1" title={d.rejection_reason || ""}><AlertCircle className="w-3 h-3" />Rejeté</span>;
    return <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 flex items-center gap-1"><AlertCircle className="w-3 h-3" />En attente</span>;
  };

  // Iter51: badge d'expiration (uniquement pour les docs validés avec date de validité).
  // Rouge = expiré, orange = expire dans <30 jours.
  const EXPIRY_BADGE = (d, requiresExpiry) => {
    // Iter51: only render when the doc explicitly requires an expiry.
    // Default to `false` if the caller omits the flag to avoid false-positive badges.
    if (!d || d.validation_status !== "validated" || requiresExpiry !== true || !d.valid_until) return null;
    const vu = new Date(d.valid_until);
    if (isNaN(vu.getTime())) return null;
    const now = new Date();
    const msPerDay = 86400000;
    const daysLeft = Math.floor((vu.getTime() - now.getTime()) / msPerDay);
    if (daysLeft < 0) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 flex items-center gap-1 font-semibold"
              data-testid={`doc-expired-${d.doc_type}`}
              title={`Expiré le ${vu.toLocaleDateString("fr-FR")}`}>
          <AlertCircle className="w-3 h-3" />Expiré
        </span>
      );
    }
    if (daysLeft < 30) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-orange-100 text-orange-700 flex items-center gap-1 font-semibold"
              data-testid={`doc-expiring-soon-${d.doc_type}`}
              title={`Expire le ${vu.toLocaleDateString("fr-FR")} (dans ${daysLeft} jour${daysLeft > 1 ? "s" : ""})`}>
          <AlertCircle className="w-3 h-3" />Expire bientôt
        </span>
      );
    }
    return null;
  };

  const nbValidated = required.filter((r) => r.required !== false && (() => {
    const d = latestByType(r.doc_type);
    return d && d.validation_status === "validated";
  })()).length;
  const nbRequired = required.filter((r) => r.required !== false).length;

  return (
    <div className="space-y-4" data-testid="mns-docs-tab">
      <ProfileStatusCard onUploadDoc={startUpload} />

      <div className="card-soft p-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Documents obligatoires</h3>
            <p className="text-sm text-slate-600">Tous les documents doivent être validés par l'administration pour que vous puissiez publier vos créneaux.</p>
          </div>
          <div className="text-sm">
            <span className="font-bold text-emerald-700">{nbValidated}</span>
            <span className="text-slate-500"> / {nbRequired} validés</span>
          </div>
        </div>
        <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${nbRequired ? (nbValidated / nbRequired) * 100 : 0}%` }} />
        </div>
      </div>

      <div className="space-y-2" data-testid="required-docs-list">
        {requiredDocs.map((r) => {
          const d = latestByType(r.doc_type);
          return (
            <div key={r.doc_type} className="card-soft p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`doc-row-${r.doc_type}`}>
              <div className="min-w-[200px]">
                <div className="font-semibold text-slate-900">{r.label}</div>
                {d ? (
                  <div className="text-xs text-slate-500 mt-0.5">
                    {d.original_filename}{r.requires_expiry !== false && d.valid_until ? ` • valide jusqu'au ${d.valid_until}` : ""}
                    {d.rejection_reason && <span className="block text-red-600 mt-1">Motif du rejet : {d.rejection_reason}</span>}
                  </div>
                ) : (
                  <div className="text-xs text-slate-400 mt-0.5">Document non fourni</div>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {STATUS_BADGE(d)}
                {EXPIRY_BADGE(d, r.requires_expiry)}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => startUpload(r.doc_type, r.label, r.requires_expiry !== false)} data-testid={`upload-${r.doc_type}`}>
                  <Upload className="w-3 h-3 mr-1" />{d ? "Remplacer" : "Téléverser"}
                </Button>
                {d && <Button size="icon" variant="ghost" onClick={() => del(d.diploma_id)} data-testid={`delete-${r.doc_type}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Iter35 wave 2: optional documents */}
      {optionalDocs.length > 0 && (
        <div className="card-soft p-4 mt-4">
          <h4 className="font-semibold text-slate-700 text-sm mb-2">Documents optionnels</h4>
          <p className="text-xs text-slate-500 mb-3">Recommandés mais non bloquants pour la validation de votre profil.</p>
          <div className="space-y-2" data-testid="optional-docs-list">
            {optionalDocs.map((r) => {
              const d = latestByType(r.doc_type);
              return (
                <div key={r.doc_type} className="bg-slate-50 rounded-xl p-3 flex items-center justify-between flex-wrap gap-3" data-testid={`doc-row-${r.doc_type}`}>
                  <div className="min-w-[200px]">
                    <div className="font-medium text-slate-800">{r.label}</div>
                    {d ? (
                      <div className="text-xs text-slate-500 mt-0.5">{d.original_filename}{r.requires_expiry !== false && d.valid_until ? ` • valide jusqu'au ${d.valid_until}` : ""}</div>
                    ) : (
                      <div className="text-xs text-slate-400 mt-0.5">Non fourni — optionnel</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {STATUS_BADGE(d)}
                    {EXPIRY_BADGE(d, r.requires_expiry)}
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => startUpload(r.doc_type, r.label, r.requires_expiry !== false)} data-testid={`upload-${r.doc_type}`}>
                      <Upload className="w-3 h-3 mr-1" />{d ? "Remplacer" : "Téléverser"}
                    </Button>
                    {d && <Button size="icon" variant="ghost" onClick={() => del(d.diploma_id)} data-testid={`delete-${r.doc_type}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {uploadingFor && (
        <form onSubmit={submit} className="card-soft p-5 space-y-3 border-2 border-sky-200" data-testid="doc-upload-form">
          <h4 className="font-bold text-sky-800" style={{ fontFamily: "Outfit" }}>
            Téléverser : {uploadingFor.label}
          </h4>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Intitulé affiché</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dup-name" /></div>
            {uploadingFor.requires_expiry !== false ? (
              <div><Label>Valide jusqu'au <span className="text-red-500">*</span></Label><Input required type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} data-testid="dup-valid" /></div>
            ) : (
              <div className="flex items-end text-xs text-slate-500 italic">Ce document n'a pas de date d'expiration.</div>
            )}
          </div>
          <div>
            <Label>Fichier (PDF / JPG / PNG, max 10 Mo) <span className="text-red-500">*</span></Label>
            <Input required type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setForm({ ...form, file: e.target.files[0] })} data-testid="dup-file" />
          </div>
          <div className="flex gap-2">
            <Button type="submit" className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="dup-submit">
              <Upload className="w-4 h-4 mr-1" />Téléverser
            </Button>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => setUploadingFor(null)}>Annuler</Button>
          </div>
        </form>
      )}
    </div>
  );
}

function BoostTab() {
  const [status, setStatus] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [busy, setBusy] = useState(false);
  const reload = async () => {
    setStatus((await api.get("/boost/status")).data);
    setWallet((await api.get("/wallet")).data);
  };
  useEffect(() => { reload(); }, []);
  const buy = async (method) => {
    setBusy(true);
    try {
      const { data } = await api.post("/boost/activate", { payment_method: method, origin_url: window.location.origin });
      if (method === "stripe" && data.url) { redirectToCheckout(data.url); return; }
      toast.success("Boost activé !"); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  if (!status) return null;
  return (
    <div className="space-y-4">
      <div className="card-soft p-6">
        <h3 className="font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Star className="w-5 h-5 text-amber-500" />Boost de visibilité</h3>
        <p className="text-sm text-slate-600 mt-2">
          Pendant 7 jours, votre profil apparaît en tête des résultats sur la carte et sur la page de chaque piscine,
          avec un badge "Boosté".
        </p>
        <div className="mt-4 flex items-baseline gap-2">
          <div className="text-4xl font-bold text-sky-700" style={{ fontFamily: "Outfit" }}>{status.price.toFixed(2)}€</div>
          <div className="text-slate-500">/ {status.days} jours</div>
        </div>
        {status.active ? (
          <div className="mt-4 bg-emerald-50 text-emerald-800 p-3 rounded-xl text-sm">
            ✅ Boost actif jusqu'au <strong>{new Date(status.boost.expires_at).toLocaleString("fr-FR")}</strong>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <Button onClick={() => buy("stripe")} disabled={busy} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="buy-boost-stripe">Payer par carte (Stripe)</Button>
            <Button onClick={() => buy("wallet")} disabled={busy || (wallet?.balance || 0) < status.price} variant="outline" className="rounded-full" data-testid="buy-boost-wallet">
              Déduire de ma cagnotte ({wallet?.balance?.toFixed(2)}€)
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function RgpdTab() {
  const { s, save } = useSettings();
  const [marketing, setMarketing] = useState(false);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { if (s) { setMarketing(!!s.marketing_consent); setDirty(false); } }, [s]);
  if (!s) return null;
  const onMarketing = (v) => { setMarketing(v); setDirty(true); };
  const submit = async () => {
    await save({ marketing_consent: marketing });
    setDirty(false);
  };
  return (
    <div className="card-soft p-6 space-y-4">
      <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Protection de vos données</h3>
      <div className="text-sm text-slate-600 space-y-2">
        <p><strong>Données collectées :</strong> nom, prénom, email, téléphone, adresse, niveau de natation, objectifs, historique de réservations et paiements.</p>
        <p><strong>Finalités :</strong> mise en relation MNS/clients, gestion des cours, paiement, facturation, comptabilité, communication relative au service.</p>
        <p><strong>Base légale :</strong> exécution du contrat (art. 6.1.b RGPD) et obligations légales (comptabilité).</p>
        <p><strong>Conservation :</strong> 3 ans après le dernier cours (10 ans pour les pièces comptables).</p>
        <p><strong>Vos droits :</strong> accès, rectification, effacement, portabilité, limitation, opposition. Pour exercer vos droits, écrivez à <a href="mailto:contact@myswimminglessons.fr" className="text-sky-700">contact@myswimminglessons.fr</a>.</p>
        <p><strong>Réclamation :</strong> auprès de la CNIL (cnil.fr).</p>
      </div>
      <div className="bg-sky-50 p-3 rounded-xl text-sm">
        <strong>Consentement de service</strong>
        <p className="text-slate-700 mt-1">En créant un compte, vous avez accepté que vos données soient utilisées pour fournir le service (réservations, messagerie, etc.). {s.rgpd_accepted_at && <span className="text-xs text-slate-500">(accepté le {new Date(s.rgpd_accepted_at).toLocaleDateString("fr-FR")})</span>}</p>
      </div>
      <label className="flex items-start gap-2 bg-slate-50 p-3 rounded-xl text-sm cursor-pointer">
        <input type="checkbox" checked={marketing} onChange={(e) => onMarketing(e.target.checked)} data-testid="marketing-toggle" className="mt-0.5" />
        <span>Je souhaite recevoir des emails à des fins marketing (newsletters, promotions, etc.). Si décochée, vos coordonnées sont retirées de la liste de diffusion.</span>
      </label>
      <Button onClick={submit} disabled={!dirty} className="rounded-full bg-sky-700 hover:bg-sky-600" data-testid="rgpd-save">
        Enregistrer les modifications
      </Button>
      <AccountActions />
    </div>
  );
}

function AccountActions() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const confirm = useConfirm();

  const doLogout = async () => {
    const ok = await confirm({
      title: "Se déconnecter ?",
      body: "Vous serez redirigé vers la page d'accueil. Vous pourrez vous reconnecter à tout moment.",
      confirmLabel: "Se déconnecter",
    });
    if (!ok) return;
    await logout();
    nav("/");
  };

  const doDelete = async () => {
    if (user?.role === "admin") {
      toast.error("Les administrateurs ne peuvent pas supprimer leur compte ici. Contactez le support.");
      return;
    }
    const ok = await confirm({
      title: "Supprimer définitivement votre compte ?",
      body: "Cette action est irréversible. Vos réservations actives seront annulées, vos données personnelles supprimées (RGPD). Les pièces comptables sont conservées 10 ans conformément à la loi.",
      confirmLabel: "Supprimer mon compte",
      tone: "warn",
    });
    if (!ok) return;
    const ok2 = await confirm({
      title: "Confirmer la suppression",
      body: "Êtes-vous absolument sûr ? Tapez sur « Supprimer définitivement » pour confirmer.",
      confirmLabel: "Supprimer définitivement",
      tone: "danger",
    });
    if (!ok2) return;
    try {
      await api.delete("/auth/me");
      toast.success("Votre compte a été supprimé. Au revoir.");
      await logout();
      nav("/");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  return (
    <div className="border-t border-slate-200 pt-4 mt-4 space-y-3" data-testid="account-actions">
      <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Mon compte</h3>
      <div className="flex flex-wrap gap-3">
        <Button variant="outline" className="rounded-full" onClick={doLogout} data-testid="profile-logout-btn">
          <LogOut className="w-4 h-4 mr-2" />Se déconnecter
        </Button>
        <Button variant="outline" className="rounded-full text-red-700 border-red-300 hover:bg-red-50 hover:text-red-800" onClick={doDelete} data-testid="profile-delete-btn">
          <Trash2 className="w-4 h-4 mr-2" />Supprimer mon compte
        </Button>
      </div>
      <p className="text-xs text-slate-500">
        La suppression annule vos réservations actives et efface vos données personnelles. Les pièces comptables (factures) sont conservées 10 ans par obligation légale.
      </p>
    </div>
  );
}


function SubscriptionTab() {
  const [data, setData] = useState(null);
  const [premium, setPremium] = useState(null);
  const [loading, setLoading] = useState(false);

  const reload = async () => {
    const [a, b] = await Promise.all([
      api.get("/subscriptions/me"),
      api.get("/subscriptions/premium-status").catch(() => ({ data: null })),
    ]);
    setData(a.data); setPremium(b.data);
  };
  useEffect(() => {
    reload();
    // Auto-sync after return from Stripe Checkout
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("session_id");
    if (params.get("subscription") === "success" && sess) {
      api.get(`/subscriptions/checkout-status/${sess}`).then(() => {
        toast.success("Abonnement activé — bienvenue parmi les MNS Premium !");
        reload();
      }).catch(() => {});
      // clean URL
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    } else if (params.get("subscription") === "cancel") {
      toast.info("Paiement annulé");
      const clean = window.location.pathname;
      window.history.replaceState({}, "", clean);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = async (plan) => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data: res } = await api.post("/subscriptions/checkout", { plan, origin_url: origin });
      if (res.url) {
        await redirectToCheckout(res.url);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur Stripe");
    } finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Résilier votre abonnement ? Il restera actif jusqu'à la fin de la période en cours.")) return;
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Résilié. Actif jusqu'à la fin de la période");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Iter37 decies: manual resync for users whose Stripe subscription wasn't auto-applied
  // (closed tab too early, network glitch, annual trial with no immediate payment, etc.).
  const resync = async () => {
    try {
      const { data: r } = await api.post("/subscriptions/resync");
      if (r.active) {
        toast.success("Abonnement synchronisé — accès Premium activé !");
      } else {
        toast.info(`Abonnement trouvé (statut: ${r.status}). Si l'accès n'est pas activé, contactez le support.`);
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aucun abonnement à synchroniser. Si vous venez de payer, attendez 30s puis réessayez.");
    }
  };

  const resume = async () => {
    try {
      await api.post("/subscriptions/resume");
      toast.success("Abonnement réactivé");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  // Iter51: débloquer 1 mois d'abonnement avec des crédits parrainage
  const redeemWithCredits = async () => {
    const cost = premium?.subscription_credits_cost || 300;
    if (!window.confirm(`Utiliser ${cost} crédits parrainage pour activer 1 mois de Premium ?`)) return;
    setLoading(true);
    try {
      const { data: r } = await api.post("/subscriptions/redeem-credits");
      toast.success(`Premium activé jusqu'au ${new Date(r.credit_premium_until).toLocaleDateString("fr-FR")}`);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  if (!data) return <div className="p-8 text-center text-slate-500">Chargement…</div>;
  const sub = data.subscription;
  const subActive = data.active;
  // Iter35: premium = paid sub OR within 90-day trial after first paid booking
  const premiumActive = !!premium?.active;
  const inTrial = premium?.source === "trial" && premiumActive;

  return (
    <div className="space-y-5" data-testid="subscription-tab">
      {/* Iter37 decies: manual resync banner — always visible at the top */}
      <div className="flex items-center justify-end">
        <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={resync} data-testid="sub-resync-btn"
                title="Vous avez payé mais l'accès Premium n'est pas activé ? Cliquez ici pour resynchroniser depuis Stripe.">
          ↻ Resynchroniser depuis Stripe
        </Button>
      </div>
      {/* Trial banner — only when within free 90-day trial */}
      {inTrial && (
        <div className="card-soft p-5 border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-cyan-50" data-testid="sub-trial-banner">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-sky-900" style={{ fontFamily: "Outfit" }}>
                Période d'essai gratuite : il vous reste <strong>{premium.trial_days_left} jour{premium.trial_days_left > 1 ? "s" : ""}</strong>
              </div>
              <p className="text-sm text-sky-800 mt-1">
                Profitez gratuitement de la <strong>CRM</strong>, des <strong>statistiques</strong>, de l'<strong>espace comptabilité</strong> et du nouvel onglet <strong>Clients</strong> jusqu'au {premium.trial_ends_at ? new Date(premium.trial_ends_at).toLocaleDateString("fr-FR") : ""}.
                <br />Souscrivez avant la fin pour conserver votre accès sans interruption.
              </p>
            </div>
          </div>
        </div>
      )}

      {premium && premium.source === "credits" && premium.active && (
        <div className="card-soft p-5 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-sky-50" data-testid="sub-credits-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>
                <Crown className="inline w-4 h-4 mr-1 text-amber-500" />
                Premium débloqué via crédits parrainage
              </div>
              <p className="text-sm text-emerald-800 mt-1">
                Actif encore <strong>{premium.credit_days_left} jour{(premium.credit_days_left || 0) > 1 ? "s" : ""}</strong>
                {premium.credit_premium_until && <> (jusqu'au {new Date(premium.credit_premium_until).toLocaleDateString("fr-FR")})</>}.
                Vous pouvez cumuler des mois supplémentaires en dépensant à nouveau {premium.subscription_credits_cost || 300} crédits.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Free state — no subscription, no trial yet */}
      {!premiumActive && !sub && (
        <div className="card-soft p-5 border-2 border-slate-200 bg-slate-50" data-testid="sub-free-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Vos créneaux sont déjà publics ✨</div>
              <p className="text-sm text-slate-700 mt-1">
                La <strong>publication des créneaux</strong> et la <strong>messagerie client</strong> sont totalement <strong>gratuites</strong>.
                <br />L'abonnement déverrouille les outils Premium : CRM, Statistiques, Comptabilité Factur-X, et le nouvel onglet <strong>Clients</strong> avec suivi pédagogique.
                <br /><strong>3 mois offerts</strong> à votre première réservation payée — sans CB requise jusque-là.
              </p>
            </div>
          </div>
        </div>
      )}

      {subActive && sub && (
        <div className="card-soft p-5 border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-cyan-50" data-testid="sub-active-banner">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-bold text-emerald-900" style={{ fontFamily: "Outfit" }}>
                <Crown className="inline w-4 h-4 mr-1 text-amber-500" />
                Abonnement Premium {data.plans.find((p) => p.key === sub.plan)?.label || sub.plan} — {sub.status === "trialing" ? "Essai gratuit" : "Actif"}
              </div>
              <p className="text-sm text-emerald-800 mt-1">
                {sub.cancel_at_period_end ? "Résilié — " : ""}
                {sub.current_period_end && <>Prochaine échéance : <strong>{new Date(sub.current_period_end).toLocaleDateString("fr-FR")}</strong></>}
                {sub.trial_end && sub.status === "trialing" && <> • Fin de la période d'essai : <strong>{new Date(sub.trial_end).toLocaleDateString("fr-FR")}</strong></>}
              </p>
              <div className="mt-3 flex gap-2">
                {sub.cancel_at_period_end ? (
                  <Button size="sm" className="rounded-full bg-emerald-700 hover:bg-emerald-600" onClick={resume} data-testid="sub-resume">Reprendre l'abonnement</Button>
                ) : (
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-300" onClick={cancel} data-testid="sub-cancel">Résilier à la fin de la période</Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {(!subActive || (sub && sub.cancel_at_period_end)) && (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sub-plans">
          {data.plans.map((p) => {
            const isYearly = p.key === "yearly";
            return (
              <div key={p.key}
                   className={`card-soft p-6 relative overflow-hidden transition ${isYearly ? "border-2 border-sky-400 shadow-lg" : ""}`}
                   data-testid={`plan-${p.key}`}>
                {isYearly && (
                  <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full shadow">
                    ★ Le meilleur plan
                  </div>
                )}
                <div className="text-xs uppercase tracking-widest text-sky-700 font-semibold">{isYearly ? "Annuel" : "Mensuel"}</div>
                <div className="mt-2 text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
                  {p.amount.toFixed(2)}<span className="text-lg text-slate-400"> €</span>
                </div>
                <div className="text-sm text-slate-500">{isYearly ? "par an (TTC)" : "par mois (TTC)"}</div>

                {isYearly && (
                  <div className="mt-3 bg-sky-50 rounded-xl p-3 text-xs text-sky-900 space-y-0.5">
                    <div className="font-semibold"><Sparkles className="inline w-3 h-3 mr-0.5" />2 mois offerts la 1ère année</div>
                    <div>Renouvellement annuel à la date anniversaire — résiliable à tout moment.</div>
                  </div>
                )}
                {!isYearly && (
                  <div className="mt-3 bg-slate-50 rounded-xl p-3 text-xs text-slate-600 space-y-0.5">
                    <div>Prélèvement immédiat de {p.amount.toFixed(2)}€.</div>
                    <div>Renouvellement mensuel tacite. Résiliable à tout moment.</div>
                  </div>
                )}

                <ul className="mt-4 space-y-1.5 text-sm text-slate-700">
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><strong>CRM & Statistiques</strong> — suivi mensuel, KPIs, exports</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><strong>Espace Comptabilité</strong> — Factures Factur-X (réforme sept. 2026), FEC CSV, récap TVA</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><strong>Onglet Clients</strong> — Liste paginée + suivi pédagogique privé (texte + photos)</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span><strong>3 mois gratuits</strong> à votre 1ère réservation payée</span></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /><span>Résiliable à tout moment</span></li>
                </ul>

                <Button
                  className={`w-full rounded-full mt-5 ${isYearly ? "bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-500 hover:to-cyan-500" : "bg-slate-900 hover:bg-slate-800"} text-white`}
                  onClick={() => subscribe(p.key)} disabled={loading}
                  data-testid={`sub-${p.key}-btn`}
                >
                  {loading ? "Redirection Stripe…" : isYearly ? "Choisir l'annuel" : "Choisir le mensuel"}
                </Button>

                {/* Iter51: option "Débloquer avec crédits parrainage" — visible uniquement sur la carte mensuelle */}
                {!isYearly && premium && (() => {
                  const cost = premium.subscription_credits_cost || 300;
                  const bal = Number(premium.referral_credits || 0);
                  const enough = bal >= cost;
                  return (
                    <Button
                      variant="outline"
                      className={`w-full rounded-full mt-2 border-emerald-400 text-emerald-800 hover:bg-emerald-50 ${enough ? "" : "opacity-50 cursor-not-allowed"}`}
                      onClick={enough ? redeemWithCredits : undefined}
                      disabled={!enough || loading}
                      title={enough ? "" : `Requiert ${cost} crédits (solde : ${bal})`}
                      data-testid="sub-monthly-credits-btn"
                    >
                      Débloquer avec {cost} crédits ({bal}/{cost})
                    </Button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {!premiumActive && (
        <div className="card-soft p-4 bg-slate-50/60">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
            <div className="text-xs text-slate-600">
              <strong>Continuer sans abonnement</strong> : vous publiez vos créneaux, recevez des réservations et discutez avec vos clients. Seuls les outils <strong>CRM / Statistiques / Comptabilité / Clients</strong> sont réservés aux abonnés Premium.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InvoicesTab() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState("all"); // all | available | pending
  useEffect(() => {
    api.get("/invoices/me").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  const downloadIcs = (bookingId) => {
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    window.location.href = `${API}/api/bookings/${bookingId}/calendar.ics`;
  };
  const downloadAllIcs = () => {
    const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    window.location.href = `${API}/api/bookings/me/calendar.ics`;
  };
  const downloadInvoice = async (bookingId) => {
    try {
      const r = await api.get(`/invoices/booking/${bookingId}/factur-x`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `facture-${bookingId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Facture indisponible");
    }
  };
  const downloadCommission = async (bookingId) => {
    try {
      const r = await api.get(`/invoices/booking/${bookingId}/commission/factur-x`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: r.headers["content-type"] || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `frais-service-${bookingId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Facture frais de service indisponible");
    }
  };
  if (!list) return <div className="p-8 text-center text-slate-500">Chargement…</div>;
  const filtered = list.filter((b) => {
    if (filter === "available") return b.available;
    if (filter === "pending") return !b.available && b.payment_status === "paid";
    return true;
  });
  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="card-soft p-5">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Mes factures</h2>
            <p className="text-sm text-slate-500">Téléchargez vos factures (cours + frais de service) au format Factur-X (PDF/A + XML CII), conforme à la réforme de septembre 2026.</p>
          </div>
          <Button variant="outline" className="rounded-full" onClick={downloadAllIcs} data-testid="dl-all-ics">
            <Share2 className="w-3 h-3 mr-1" />Ajouter tous mes cours à mon agenda (.ics)
          </Button>
        </div>
        <div className="flex gap-2 mt-3">
          {[["all", "Tous"], ["available", "Disponibles"], ["pending", "En attente de validation"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1 rounded-full border ${filter === v ? "bg-sky-700 text-white border-sky-700" : "bg-white text-slate-600 hover:bg-slate-50"}`}
              data-testid={`invoices-filter-${v}`}>{l}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="card-soft p-8 text-center text-slate-500">Aucune facture pour ce filtre.</div>
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left p-3">N° Facture</th>
                <th className="text-left p-3">Date du cours</th>
                <th className="text-left p-3">Lieu</th>
                <th className="text-right p-3">Cours</th>
                <th className="text-right p-3">Frais service</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => (
                <tr key={b.booking_id} className="border-t" data-testid={`inv-${b.booking_id}`}>
                  <td className="p-3 font-mono text-xs">{b.invoice_number}</td>
                  <td className="p-3 whitespace-nowrap">{new Date(b.start_dt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="p-3">{b.pool_name || "—"}{b.city ? <div className="text-xs text-slate-400">{b.city}</div> : null}</td>
                  <td className="p-3 text-right font-medium">{b.amount_eur.toFixed(2)}€</td>
                  <td className="p-3 text-right text-slate-600">{b.service_fee_eur.toFixed(2)}€</td>
                  <td className="p-3">
                    {b.available ? (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Disponible</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{b.availability_message?.slice(0, 60) || "Indisponible"}</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex flex-wrap gap-1.5 justify-end">
                      <Button size="sm" variant="outline" className="rounded-full" disabled={!b.available} onClick={() => downloadInvoice(b.booking_id)} data-testid={`dl-inv-${b.booking_id}`}>
                        Facture cours
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" disabled={!b.available} onClick={() => downloadCommission(b.booking_id)} data-testid={`dl-comm-${b.booking_id}`}>
                        Frais service
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => downloadIcs(b.booking_id)} data-testid={`ics-${b.booking_id}`} title="Ajouter à mon agenda">
                        .ics
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ReferralTab() {
  const [data, setData] = useState(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferForm, setTransferForm] = useState({ email: "", credits: 10, message: "" });
  const [transferBusy, setTransferBusy] = useState(false);
  const reload = () => api.get("/referral/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copié !"); }
    catch { toast.error("Impossible de copier"); }
  };
  const doTransfer = async () => {
    setTransferBusy(true);
    try {
      const { data: r } = await api.post("/referral/transfer", {
        recipient_email: transferForm.email.trim().toLowerCase(),
        credits: Number(transferForm.credits),
        message: transferForm.message.trim(),
      });
      toast.success(`Transfert effectué. Nouveau solde : ${r.new_balance} crédits.`);
      setTransferOpen(false);
      setTransferForm({ email: "", credits: 10, message: "" });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfert impossible");
    } finally { setTransferBusy(false); }
  };
  if (!data) return <div className="p-8 text-center text-slate-500">Chargement…</div>;
  const credits = Number(data.referral_credits || 0);
  const shareUrl = `${window.location.origin}/register?ref=${encodeURIComponent(data.referral_code || "")}`;
  return (
    <div className="space-y-5" data-testid="referral-tab">
      {/* Hero card */}
      <div className="rounded-2xl p-6 overflow-hidden relative text-white shadow-lg border border-sky-200"
           style={{ background: "linear-gradient(135deg, #0ea5e9 0%, #06b6d4 50%, #14b8a6 100%)" }}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-xs font-medium backdrop-blur">
              <Gift className="w-3.5 h-3.5" /> Programme parrainage MySwimmingLessons
            </div>
            <h2 className="text-3xl font-bold mt-3" style={{ fontFamily: "Outfit" }}>
              {data.bonus_credits} crédits offerts à vous et à votre filleul·e
            </h2>
            <p className="opacity-90 mt-1 text-sm max-w-md">
              Partagez votre code. À la 1ère réservation payée de votre filleul, vous recevez tous les deux {data.bonus_credits} crédits.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest opacity-80">Mon solde</div>
            <div className="text-5xl font-bold" style={{ fontFamily: "Outfit" }} data-testid="referral-credits">{credits}</div>
            <div className="text-[11px] opacity-80">crédits</div>
          </div>
        </div>
      </div>

      {/* Règles d'utilisation — texte éditable par l'admin (fallback aux règles statiques si vide).
          Iter40 ter: sanitization via SafeHtml (DOMPurify) pour éliminer la surface XSS. */}
      <div className="card-soft p-5 bg-amber-50 border border-amber-200">
        {data.terms_text && data.terms_text.trim().length > 0 ? (
          <SafeHtml
            html={data.terms_text}
            className="prose prose-sm max-w-none text-slate-700"
            testId="referral-terms-text"
          />
        ) : (
          <>
            <h3 className="font-bold text-amber-900 mb-2 text-sm uppercase tracking-widest">Règles d'utilisation des crédits</h3>
            <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
              <li><strong>Comment gagner des crédits :</strong> 1 parrainage validé (1ère résa payée du filleul) = <strong>{data.bonus_credits} crédits</strong> pour le parrain ET le filleul.</li>
              <li><strong>{data.redeem_service_fee_cost} crédits</strong> = les <strong>frais de service</strong> (commission plateforme 10%) sont <strong>offerts</strong> sur un cours de votre choix.</li>
              <li><strong>Réservé aux maîtres-nageurs (MNS) :</strong> 100 crédits = 1 semaine de boost visibilité · 300 crédits = 1 mois d'abonnement Premium.</li>
              <li><strong>Transfert :</strong> vous pouvez transférer vos crédits à un autre client en renseignant son adresse email. Il reçoit une notification + email.</li>
              <li>Les crédits n'expirent pas. Ils sont utilisables uniquement sur la plateforme et ne sont pas convertibles en argent.</li>
            </ul>
          </>
        )}
        <div className="flex gap-2 mt-3 flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTransferOpen(true)} disabled={credits < 1} data-testid="open-transfer">
            <Share2 className="w-3 h-3 mr-1" />Transférer des crédits
          </Button>
        </div>
      </div>

      {/* Code + share */}
      <div className="grid md:grid-cols-[auto_1fr] gap-4">
        <div className="card-soft p-6 text-center">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Votre code</div>
          <div className="font-mono font-bold text-3xl text-sky-900 tracking-wider select-all" data-testid="referral-code">{data.referral_code}</div>
          <Button size="sm" variant="outline" className="rounded-full mt-3" onClick={() => copy(data.referral_code)} data-testid="copy-code">
            <Copy className="w-3 h-3 mr-1" />Copier le code
          </Button>
        </div>
        <div className="card-soft p-6 space-y-3">
          <Label>Lien de parrainage direct (le code est pré-rempli)</Label>
          <div className="flex gap-2">
            <Input value={shareUrl} readOnly className="font-mono text-xs" data-testid="share-url" />
            <Button variant="outline" className="rounded-full whitespace-nowrap" onClick={() => copy(shareUrl)}>
              <Copy className="w-3 h-3 mr-1" />Copier
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`https://wa.me/?text=${encodeURIComponent("Rejoins-moi sur MySwimmingLessons et reçois 10 crédits offerts à ta 1ère réservation ! " + shareUrl)}`} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-sm hover:bg-emerald-50 hover:text-emerald-600 transition"><Share2 className="w-3.5 h-3.5" />WhatsApp</a>
            <a href={`mailto:?subject=${encodeURIComponent("10 crédits offerts sur MySwimmingLessons")}&body=${encodeURIComponent("Salut,\n\nInscris-toi avec mon code parrainage pour recevoir 10 crédits dès ta 1ère réservation : " + shareUrl)}`}
               className="inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-sm hover:bg-slate-50 transition"><Share2 className="w-3.5 h-3.5" />Email</a>
          </div>
        </div>
      </div>

      {data.parrain_name && (
        <div className="card-soft p-4 bg-sky-50/50 text-sm text-sky-900" data-testid="referral-parrain">
          <Gift className="inline w-4 h-4 mr-1" />Vous avez été parrainé·e par <strong>{data.parrain_name}</strong>.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-soft p-5">
          <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>
            Mes filleul·es {data.filleuls.length > 0 && <span className="text-sky-600">({data.filleuls.length})</span>}
          </h3>
          {data.filleuls.length === 0 ? (
            <p className="text-sm text-slate-500">Personne pour le moment. Partagez votre code !</p>
          ) : (
            <div className="space-y-2" data-testid="filleuls-list">
              {data.filleuls.map((f) => (
                <div key={f.user_id} className="flex items-center justify-between gap-2 p-2 rounded-lg hover:bg-slate-50">
                  <div>
                    <div className="font-medium text-sm">{f.name}</div>
                    <div className="text-[11px] text-slate-400">Inscrit le {new Date(f.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  {f.credited ? (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700"><CheckCircle2 className="inline w-3 h-3 mr-0.5" />+{data.bonus_credits} crédits</span>
                  ) : (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">En attente d'une 1ère résa</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="card-soft p-5">
          <h3 className="font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Historique des crédits</h3>
          {data.transactions.length === 0 ? (
            <p className="text-sm text-slate-500">Aucun mouvement.</p>
          ) : (
            <div className="space-y-1 text-sm" data-testid="referral-history">
              {data.transactions.map((t) => (
                <div key={t.tx_id} className="flex items-center justify-between p-2 border-b last:border-0">
                  <div>
                    <div>{t.reason}{t.other_party_name && <span className="text-slate-500"> — {t.other_party_name}</span>}</div>
                    <div className="text-[11px] text-slate-400">{new Date(t.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                  <div className={`font-semibold ${t.amount < 0 ? "text-red-600" : "text-emerald-700"}`}>{t.amount > 0 ? "+" : ""}{t.amount}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={transferOpen} onOpenChange={(v) => !v && setTransferOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transférer des crédits</DialogTitle>
            <DialogDescription>
              Solde actuel : <strong>{credits} crédits</strong>. Le destinataire doit être un client inscrit avec cet email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Email du destinataire</Label>
              <Input type="email" value={transferForm.email} onChange={(e) => setTransferForm({ ...transferForm, email: e.target.value })} placeholder="ami@example.com" data-testid="transfer-email" />
            </div>
            <div>
              <Label>Nombre de crédits</Label>
              <Input type="number" min="1" max={credits} value={transferForm.credits} onChange={(e) => setTransferForm({ ...transferForm, credits: Number(e.target.value) })} data-testid="transfer-credits" />
            </div>
            <div>
              <Label>Message (optionnel)</Label>
              <textarea className="w-full border rounded-xl p-3 text-sm" rows={2} maxLength={300}
                value={transferForm.message} onChange={(e) => setTransferForm({ ...transferForm, message: e.target.value })}
                placeholder="Cadeau pour ton anniversaire 🎁" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTransferOpen(false)}>Annuler</Button>
            <Button className="rounded-full bg-sky-700 hover:bg-sky-600 text-white"
              onClick={doTransfer}
              disabled={transferBusy || !transferForm.email || transferForm.credits < 1 || transferForm.credits > credits}
              data-testid="confirm-transfer">
              {transferBusy ? "Envoi…" : `Transférer ${transferForm.credits} crédits`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

